// #130 "Rona AI Sıralama Optimizasyonu" modalı — POST /api/shipmentplans/optimize
// ile seçili aracın sıralı noktalarını gönderir; Eski Sıra ↔ Yeni Sıra yan yana +
// Eski/Yeni KM + kazanç gösterir. "Uygula" araç duraklarını yeni sıraya dizer.
import { useEffect, useMemo, useState } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { API_BASE, unwrap, apiMessage, fmtNumber, analyzeTraffic } from './shipmentPlanningUtils';

export default function OptimizeModal({ vehicle, onClose, onApply, showToast }) {
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [departure, setDeparture] = useState('09:00');
  const [weekend, setWeekend] = useState(false);

  // Aracın mevcut sırasına göre noktalar.
  const stops = (vehicle?.orders || []).slice().sort((a, b) => (a.visitOrderNo ?? 9999) - (b.visitOrderNo ?? 9999));
  const valid = stops.filter((o) => Number.isFinite(o.customerLat) && Number.isFinite(o.customerLng));

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetch(`${API_BASE}/optimize`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ points: valid.map((o) => ({ lat: o.customerLat, lng: o.customerLng })) }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!alive) return;
        if (!ok) throw new Error(apiMessage(d, 'Optimizasyon başarısız'));
        setResult(unwrap(d));
      })
      .catch((e) => { if (alive) { showToast?.('error', e.message); onClose?.(); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const oldOrder = result?.oldOrder || [];
  const newOrder = result?.newOrder || [];
  const oldKm = Number(result?.oldKm) || 0;
  const newKm = Number(result?.newKm) || 0;
  const gain = oldKm - newKm;
  const gainPct = oldKm > 0 ? (gain / oldKm) * 100 : 0;

  // Optimize edilmiş rota üzerinden sezgisel trafik analizi.
  const analysisKm = newKm || oldKm;
  const traffic = useMemo(
    () => analyzeTraffic({ km: analysisKm, departure, weekend }),
    [analysisKm, departure, weekend],
  );
  const extraMin = Math.max(0, Math.round(traffic.trafficMin - traffic.freeFlowMin));

  // newOrder = eski indekslere göre yeni sıra → araç order'larını yeniden diz.
  const apply = () => {
    if (!newOrder.length) { onClose?.(); return; }
    const reordered = newOrder.map((idx, i) => ({ ...valid[idx], visitOrderNo: i + 1 }));
    // Konumu olmayanları (optimizasyona girmeyen) sona ekle.
    const missing = stops.filter((o) => !valid.includes(o)).map((o, i) => ({ ...o, visitOrderNo: reordered.length + i + 1 }));
    onApply?.([...reordered, ...missing]);
    showToast?.('success', `Sıralama uygulandı — ${fmtNumber(Math.max(0, gain), 1)} km kazanç.`);
    onClose?.();
  };

  return (
    <div style={s.overlay} onClick={onClose}>
      <div style={{ ...s.modal, maxWidth: 640, width: '94%' }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modalHeader}>
          <div>
            <h3 style={s.modalTitle}>✨ Rona AI Sıralama Optimizasyonu</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#6b7280)' }}>
              {vehicle?.plaka} — en kısa rota için durak sırası önerisi.
            </p>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={s.modalBody}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary,#94a3b8)' }}>Hesaplanıyor…</div>
          ) : (
            <>
              {/* KM özet */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                <Metric label="Eski KM" value={`${fmtNumber(oldKm, 1)} km`} color="#64748b" />
                <Metric label="Yeni KM" value={`${fmtNumber(newKm, 1)} km`} color="var(--accent)" />
                <Metric label="Kazanç" value={`${fmtNumber(Math.max(0, gain), 1)} km · %${fmtNumber(Math.max(0, gainPct), 0)}`} color={gain > 0 ? '#16a34a' : '#64748b'} />
              </div>

              {/* Eski ↔ Yeni sıra */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 8 }}>
                <SeqList title="Eski Sıra" seq={oldOrder.length ? oldOrder : valid.map((_, i) => i)} stops={valid} accent="#94a3b8" />
                <SeqList title="Yeni Sıra" seq={newOrder} stops={valid} accent="#16a34a" />
              </div>

              {/* Trafik analizi (sezgisel, saat-bazlı) */}
              {analysisKm > 0 && (
                <div style={{ marginTop: 12, border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, padding: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13.5, fontWeight: 800, color: 'var(--text-primary,#0f172a)' }}>🚦 Trafik Analizi</span>
                    <span style={{ fontSize: 10.5, fontWeight: 600, color: 'var(--text-secondary,#94a3b8)' }}>tahmini · saat-bazlı (canlı değil)</span>
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: 'var(--text-secondary,#475569)' }}>
                        Çıkış
                        <input type="time" value={departure} onChange={(e) => setDeparture(e.target.value)}
                          style={{ padding: '5px 8px', borderRadius: 8, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 12.5 }} />
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 700, color: 'var(--text-secondary,#475569)', cursor: 'pointer' }}>
                        <input type="checkbox" checked={weekend} onChange={(e) => setWeekend(e.target.checked)} /> Hafta sonu
                      </label>
                    </span>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
                    <Metric label="Durum" value={`${traffic.level.emoji} ${traffic.level.label}`} color={traffic.level.color} sub={`×${fmtNumber(traffic.factor, 2)}`} />
                    <Metric label="Serbest akış" value={`${Math.round(traffic.freeFlowMin)} dk`} color="#64748b" />
                    <Metric label="Trafikli tahmin" value={`${Math.round(traffic.trafficMin)} dk`} color={traffic.level.color} sub={extraMin > 0 ? `+${extraMin} dk` : 'fark yok'} />
                    <Metric label="Tahmini varış" value={traffic.arrival} color="var(--accent)" sub={`çıkış ${departure}`} />
                  </div>

                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 10, fontSize: 12, fontWeight: 700 }}>
                    {traffic.best && <span style={{ color: '#16a34a' }}>🟢 En uygun çıkış: {traffic.best.time} (~{Math.round(traffic.best.min)} dk)</span>}
                    {traffic.avoid && <span style={{ color: 'var(--danger)' }}>🔴 Kaçınılacak: {traffic.avoid}</span>}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div style={s.modalFooter}>
          <button style={s.btnSecondary} onClick={onClose}>Vazgeç</button>
          <button style={{ ...s.btnPrimary, opacity: loading || !newOrder.length ? 0.55 : 1 }} disabled={loading || !newOrder.length} onClick={apply}>
            Uygula
          </button>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color, sub }) {
  return (
    <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, padding: '10px 12px', textAlign: 'center' }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary,#94a3b8)' }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 900, color }}>{value}</div>
      {sub && <div style={{ fontSize: 10.5, fontWeight: 700, color: 'var(--text-secondary,#94a3b8)', marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

function SeqList({ title, seq, stops, accent }) {
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-secondary,#475569)', marginBottom: 6 }}>{title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
        {seq.map((idx, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 9px', border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 9, fontSize: 12.5 }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: accent, color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 11, flexShrink: 0 }}>{i + 1}</span>
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{stops[idx]?.customerName || `Durak ${idx + 1}`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
